import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Fade,
  CircularProgress,
} from '@mui/material';
import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { AuditLoggers } from '../../utils/auditLogger';
import HarmonateLogo from '../common/HarmonateLogo';
import MFADialog from './MFADialog';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateFormFieldsSx,
  harmonateCardSx,
} from '../../theme/harmonateStyles';
import { setServerIdentity } from '../../auth/serverIdentity';

// Encryption key (should ideally be imported from a shared module or environment)
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

// API base URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Utility function to ensure consistent key generation
const generateKey = () => {
  const keyBytes = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
  const hash = CryptoJS.SHA256(keyBytes);
  return CryptoJS.lib.WordArray.create(hash.words.slice(0, 8));
};

// Encrypt data
const encryptData = (data) => {
  if (!data) return '';
  try {
    const stringData = data.toString();
    const key = generateKey();
    const encrypted = CryptoJS.AES.encrypt(stringData, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Encryption failed: ' + error.message);
  }
};

// Get or generate a browser-specific identifier
// Since browsers don't have direct access to computer name,
// we'll generate/store a unique identifier for this browser instance
const getComputerName = () => {
  const storageKey = 'browser_computer_id';
  let computerId = localStorage.getItem(storageKey);
  
  // Remove old cached values that have "DESKTOP-" prefix
  if (computerId && computerId.startsWith('DESKTOP-')) {
    computerId = null;
    localStorage.removeItem(storageKey);
  }
  
  if (!computerId) {
    // Generate a unique ID based on browser fingerprint
    // Using more browser-specific properties to ensure uniqueness per browser
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);
    
    // Combine with browser-specific info that differs between browsers
    const fingerprint = [
      navigator.userAgent,           // Different for Chrome vs Edge vs Firefox
      navigator.vendor,              // Different for Chrome vs Edge
      navigator.platform,             // OS platform
      navigator.language,             // Language settings
      navigator.hardwareConcurrency || 'unknown', // CPU cores
      navigator.deviceMemory || 'unknown',        // RAM (if available)
      window.screen.width + 'x' + window.screen.height + 'x' + window.screen.colorDepth,
      window.screen.pixelDepth || window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),            // Canvas fingerprint
      navigator.webdriver || 'false', // Whether automated browser
      window.chrome ? 'chrome' : (window.navigator.userAgent.includes('Edg') ? 'edge' : 'other'),
    ].join('|');
    
    // Use SHA-256 hash for better uniqueness (CryptoJS is already imported)
    const hash = CryptoJS.SHA256(fingerprint);
    // Take first 12 characters of hex string for better uniqueness
    computerId = hash.toString(CryptoJS.enc.Hex).substring(0, 12).toUpperCase();
    localStorage.setItem(storageKey, computerId);
  }
  
  return computerId;
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Check MFA status before login
  // Returns: { success: true, mfaRequired: boolean } on success
  // Throws error if API call fails
  const checkMFAStatus = async (email) => {
    const computerName = getComputerName();
    const mfaUrl = `${API_BASE_URL}conductor/mfa-status`;
    
    console.log('Checking MFA status:', { email, computerName, url: mfaUrl });
    
    const mfaResponse = await fetch(mfaUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        currentComputerName: computerName,
      }),
    });

    console.log('MFA status response:', {
      status: mfaResponse.status,
      statusText: mfaResponse.statusText,
      ok: mfaResponse.ok
    });

    if (!mfaResponse.ok) {
      const errorText = await mfaResponse.text();
      console.error('MFA status check failed:', {
        status: mfaResponse.status,
        statusText: mfaResponse.statusText,
        error: errorText
      });
      throw new Error(`MFA status check failed: ${mfaResponse.status} ${mfaResponse.statusText}`);
    }

    const mfaData = await mfaResponse.json();
    console.log('MFA status data:', mfaData);
    
    return {
      success: true,
      mfaRequired: mfaData.mfaRequired === true
    };
  };

  // Perform the actual login
  const performLogin = async (username, password) => {
    // Encrypt the entered password
    const encryptedPassword = encryptData(password);

    // Get the current computer name
    const currentComputerName = getComputerName();

    // Prepare the payload with applicationId hardcoded as 1
    const payload = {
      username,
      password: encryptedPassword,
      applicationId: 1, // Hardcoded as 1
      application: "Conductor",
      currentComputerName: currentComputerName,
    };

    // Make the API call
    const response = await fetch(`${API_BASE_URL}auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle specific error cases
      if (response.status === 401) {
        // Check if the error message is in the jwt field (as shown in the network response)
        if (errorData.jwt && errorData.jwt.includes('Invalid Credentials')) {
          throw new Error('Invalid Credentials');
        }
        // Check for other common 401 error messages
        if (errorData.message && errorData.message.includes('Invalid Credentials')) {
          throw new Error('Invalid Credentials');
        }
        // Default 401 message
        throw new Error('Invalid Credentials');
      }
      
      // For other HTTP errors, use the message from the response or default message
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Extract the token from the API response
    const token = data.jwt; // Use data.jwt based on the response structure
    if (!token) {
      throw new Error('No token received from the server');
    }

    // Store basic login details in localStorage FIRST
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('token', token); // Store the token directly without encryption
    localStorage.setItem('application', 'Sync Track');

    console.log('Login successful, token stored');

    // Fetch current user info and store required values
    let userData = null; // Declare userData outside the if block
    try {
      const userResp = await fetch(`${API_BASE_URL}conductor/get-current-user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (userResp.ok) {
        userData = await userResp.json();
        
        // Log successful login with user details
        try {
          await AuditLoggers.userLoginSuccess(
            userData.username || username,
            '', // IP address can be captured on the server side
            {
              userId: userData.id,
              tenantId: userData.tenant_id,
              tenantName: userData.tenant_details?.name,
              isHarmonateUser: userData.harmonate_user || false,
              firstName: userData.first_name,
              lastName: userData.last_name,
              email: userData.email,
              lastLogin: userData.updated_at
            }
          );
          
          // Update local storage with user details for future audit logs
          if (userData.tenant_id) {
            localStorage.setItem('tenant_id', userData.tenant_id);
          }
          if (userData.username) {
            localStorage.setItem('username', userData.username);
          }
        } catch (logError) {
          console.error('Failed to log login event:', logError);
          // Don't block login if logging fails
        }
        
        // Store user details
        if (userData.id) {
          localStorage.setItem('user_id', userData.id);
          console.log('User ID stored:', userData.id);
        }
        if (userData.tenant_id) localStorage.setItem('tenant_id', userData.tenant_id);
        
        // Important: Store harmonate_user flag for permission system
        if (userData.harmonate_user !== undefined) {
          setServerIdentity({ harmonateUser: userData.harmonate_user });
          console.log('Harmonate user flag:', userData.harmonate_user);
        } else {
          // Default to false if not specified
          setServerIdentity({ harmonateUser: false });
          console.log('Harmonate user flag defaulted to false');
        }
        
        if (userData.tenant_details && userData.tenant_details.database) {
          localStorage.setItem('tenant_integration', userData.tenant_details.database);
        }
        if (userData.tenant_details && userData.tenant_details.name) {
          localStorage.setItem('tenant_name', userData.tenant_details.name);
        }

        // Store additional user info for display purposes
        if (userData.first_name) localStorage.setItem('first_name', userData.first_name);
        if (userData.last_name) localStorage.setItem('last_name', userData.last_name);
        if (userData.email) localStorage.setItem('email', userData.email);
        if (userData.username) localStorage.setItem('username', userData.username);

        // Log user type for debugging
        console.log('User logged in:', {
          username: userData.username,
          isHarmonateUser: userData.harmonate_user,
          userId: userData.id
        });

    } else {
      console.warn('Failed to fetch user details, but login was successful');
      // Set default harmonate_user to false if we can't fetch user details
      setServerIdentity({ harmonateUser: false });
      // Create a minimal userData object with defaults
      userData = {
        id: null,
        username: username,
        harmonate_user: false
      };
    }
    } catch (err) {
      console.error('Failed to fetch current user info after login:', err);
      // Set default harmonate_user to false on error
      setServerIdentity({ harmonateUser: false });
      // Create a minimal userData object with defaults
      userData = {
        id: null,
        username: username,
        harmonate_user: false
      };
      // Continue with login process even if user details fetch fails
    }

    // Show success message
    toast.success('Login successful!');
    
    // CRITICAL FIX: Call the AuthContext login function to update React state
    const loginSuccess = await login(token, {
      id: userData?.id,
      username: userData?.username || username,
      harmonate_user: userData?.harmonate_user || false
    });
    
    if (!loginSuccess) {
      console.error('Failed to update auth context state');
      toast.error('Login state update failed');
      throw new Error('Login state update failed');
    }
    
    // Dispatch login success event for UserRightsContext
    console.log('Dispatching login-success event');
    window.dispatchEvent(new Event('login-success'));
    
    // CRITICAL: Directly trigger user rights fetch for non-harmonate users
    if (!userData?.harmonate_user) {
      console.log('Non-harmonate user detected, directly triggering user rights fetch');
      // Dispatch a custom event with user data
      window.dispatchEvent(new CustomEvent('user-rights-fetch', {
        detail: {
          userId: userData?.id,
          isHarmonate: false
        }
      }));
    }
    
    // Small delay to ensure all localStorage items are set before navigation
    setTimeout(() => {
      // Navigate to dashboard
      navigate('/dashboard');
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setMfaError('');

    try {
      // Store credentials temporarily
      const tempCredentials = { username, password };
      setPendingCredentials(tempCredentials);

      // Check MFA status first - only proceed if API call succeeds
      const mfaResult = await checkMFAStatus(username);

      if (mfaResult.mfaRequired) {
        // MFA is required - show dialog
        setLoading(false);
        setMfaDialogOpen(true);
        return;
      }

      // MFA not required - proceed with login
      await performLogin(username, password);
      setPendingCredentials(null);
      // Note: loading will be false after navigation, but set it here for safety
      setLoading(false);
    } catch (error) {
      console.error('Error during login:', error);
      const errorMessage = error.message || 'Login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setPendingCredentials(null);
      setLoading(false);
    }
  };

  // Verify MFA code
  const verifyMFACode = async (email, code) => {
    const verifyUrl = `${API_BASE_URL}conductor/mfa-code-verify`;
    
    console.log('Verifying MFA code:', { email, code, url: verifyUrl });
    
    const verifyResponse = await fetch(verifyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        code: code,
        currentComputerName: getComputerName(),
      }),
    });

    console.log('MFA verification response:', {
      status: verifyResponse.status,
      statusText: verifyResponse.statusText,
      ok: verifyResponse.ok
    });

    if (!verifyResponse.ok) {
      const errorData = await verifyResponse.json().catch(() => ({}));
      console.error('MFA verification failed:', {
        status: verifyResponse.status,
        statusText: verifyResponse.statusText,
        error: errorData
      });
      throw new Error(errorData.message || `MFA verification failed: ${verifyResponse.status} ${verifyResponse.statusText}`);
    }

    const verifyData = await verifyResponse.json();
    console.log('MFA verification data:', verifyData);

    // Check if verification is successful
    if (verifyData.status === 'SUCCESS' && verifyData.message === 'MFA verified - proceed to login') {
      return { success: true };
    } else {
      throw new Error(verifyData.message || 'MFA verification failed');
    }
  };

  const handleMFAVerify = async (code) => {
    setMfaLoading(true);
    setMfaError('');

    try {
      if (!pendingCredentials) {
        throw new Error('No pending credentials found');
      }

      // Verify the MFA code
      await verifyMFACode(pendingCredentials.username, code);
      
      // Close dialog
      setMfaDialogOpen(false);
      
      // Proceed with login
      setLoading(true);
      await performLogin(pendingCredentials.username, pendingCredentials.password);
      setPendingCredentials(null);
      setLoading(false);
    } catch (error) {
      console.error('MFA verification error:', error);
      setMfaError(error.message || 'Verification failed. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMFAClose = () => {
    setMfaDialogOpen(false);
    setMfaError('');
    setPendingCredentials(null);
  };

  const handleRegisterRedirect = () => {
    navigate('/register');
  };

  const handleForgotPassword = () => {
    setForgotPasswordOpen(true);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        background: ht.fadeGradient,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <HarmonateLogo
          variant="white"
          height={88}
          sx={{ width: 'auto', maxWidth: 280, maxHeight: 88 }}
        />
      </Box>

      {/* Login Card */}
      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          width: '100%',
          maxWidth: 360,
          bgcolor: ht.white,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3.5,
        }}
      >
        <Typography
          component="h1"
          variant="h6"
          sx={{
            mb: 2.5,
            fontWeight: 700,
            fontSize: '1.25rem',
            color: ht.text,
          }}
        >
          Login
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ width: '100%', ...harmonateFormFieldsSx }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontSize: '0.8rem',
              color: ht.text,
              fontWeight: 500,
            }}
          >
            Username
          </Typography>
          <TextField
            required
            fullWidth
            id="username"
            name="username"
            size="small"
            placeholder="admin"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: loading ? ht.drawerBg : ht.listBg,
              },
              '& .MuiInputBase-input': {
                fontSize: '0.85rem',
                py: 1,
              },
            }}
          />

          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontSize: '0.8rem',
              color: ht.text,
              fontWeight: 500,
            }}
          >
            Password
          </Typography>
          <TextField
            required
            fullWidth
            name="password"
            type="password"
            id="password"
            placeholder="••••••••"
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: loading ? ht.drawerBg : ht.listBg,
              },
              '& .MuiInputBase-input': {
                fontSize: '0.85rem',
                py: 1,
              },
            }}
          />

          {error && (
            <Fade in={!!error}>
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: ht.buttonRadius,
                  backgroundColor: 'rgba(239, 63, 63, 0.1)',
                  color: ht.red,
                  border: `1px solid ${ht.red}`,
                  '& .MuiAlert-icon': {
                    color: ht.red,
                  },
                }}
              >
                {error}
              </Alert>
            </Fade>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              ...harmonateProceedButtonSx,
              mt: 1,
              mb: 1,
              py: 1,
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Logging in...
              </Box>
            ) : (
              'Login'
            )}
          </Button>

          <Typography
            variant="body2"
            align="center"
            sx={{
              cursor: 'pointer',
              color: ht.text,
              fontSize: '0.75rem',
              fontWeight: 600,
              mt: 1,
              '&:hover': {
                textDecoration: 'underline',
                color: ht.green,
              },
            }}
            onClick={handleForgotPassword}
          >
            Forgot password?
          </Typography>
        </Box>
      </Paper>

      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
      />

      {/* MFA Dialog */}
      <MFADialog
        open={mfaDialogOpen}
        onClose={handleMFAClose}
        onVerify={handleMFAVerify}
        email={username}
        loading={mfaLoading}
        error={mfaError}
      />
    </Box>
  );
};

export default Login;